import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, CalendarDays, Clock3, Edit3, Eye } from 'lucide-react';
import { PUBLIC_CONTENT_PAGE_MAX_WIDTH } from '../utils/publicPageLayout';
import { stripHtmlToText } from '../utils/seo';
import { guideArticleBodyStyles } from './guideArticleBodyStyles';

const GuidePageContainer = styled.div`
  width: min(${PUBLIC_CONTENT_PAGE_MAX_WIDTH}, calc(100% - (var(--site-gutter) * 2)));
  margin: 0 auto;
  padding: clamp(24px, 4vw, 48px) 0 clamp(24px, 3vw, 36px);
`;

const UtilityRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #0f766e;
  text-decoration: none;
  font-weight: 600;

  &:hover {
    color: #115e59;
  }
`;

const EditLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 999px;
  text-decoration: none;
  background: #0f766e;
  color: white;
  font-weight: 600;

  &:hover {
    background: #115e59;
  }
`;

const PreviewBanner = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
  padding: 10px 14px;
  border-radius: 999px;
  background: #ecfeff;
  border: 1px solid #a5f3fc;
  color: #155e75;
  font-size: 14px;
  font-weight: 600;
`;

const HeroCard = styled.article`
  overflow: hidden;
  border-radius: 28px;
  background: white;
  border: 1px solid #dbe4ea;
  box-shadow: 0 22px 54px rgba(15, 23, 42, 0.08);
  margin-bottom: 24px;
`;

const CoverImage = styled.img`
  display: block;
  width: 100%;
  max-height: 420px;
  object-fit: cover;
  background: linear-gradient(135deg, #d6f6f0 0%, #eff6ff 100%);
`;

const HeroContent = styled.div`
  padding: clamp(24px, 3vw, 40px);
  display: grid;
  gap: 18px;
`;

const MetaChips = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const MetaChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 999px;
  background: ${props => props.$tone === 'preview' ? '#eef2ff' : props.$tone === 'status' ? '#fff7ed' : '#ecfeff'};
  color: ${props => props.$tone === 'preview' ? '#4338ca' : props.$tone === 'status' ? '#9a3412' : '#155e75'};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  color: #0f172a;
  font-size: clamp(2.2rem, 4vw, 3.6rem);
  line-height: 1.05;
  letter-spacing: -0.03em;
`;

const Excerpt = styled.p`
  color: #475569;
  font-size: 1.08rem;
  line-height: 1.8;
  max-width: 72ch;
`;

const MetaInfoRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const MetaInfo = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 14px;
  background: #f8fafc;
  color: #334155;
  font-size: 14px;
  font-weight: 500;
`;

const BodyCard = styled.section`
  background: white;
  border-radius: 24px;
  border: 1px solid #dbe4ea;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.05);
  padding: clamp(22px, 3vw, 36px);
`;

const ArticleBody = styled.div`
  ${guideArticleBodyStyles}
`;

const Footer = styled.div`
  text-align: center;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
  color: #6b7280;
  font-size: 0.9rem;
`;

const formatGuideDate = (value) => {
  if (!value) {
    return '';
  }

  const normalizedValue = String(value).includes('T')
    ? value
    : `${String(value).replace(' ', 'T')}Z`;
  const parsedDate = new Date(normalizedValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return '';
  }

  return parsedDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const estimateReadTime = (value) => {
  const words = stripHtmlToText(value).split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 220));
};

const humanizeStatus = (status) => {
  switch (status) {
    case 'scheduled':
      return 'Scheduled';
    case 'unpublished':
      return 'Unpublished';
    case 'draft':
      return 'Draft';
    default:
      return '';
  }
};

const GuideArticleView = ({
  guide,
  previewLabel = '',
  showEditButton = false,
  editTarget = '',
}) => {
  const readTimeMinutes = useMemo(() => estimateReadTime(guide?.content_html || ''), [guide?.content_html]);
  const publishedLabel = formatGuideDate(guide?.published_at || guide?.created_at);
  const updatedLabel = formatGuideDate(guide?.updated_at);
  const statusLabel = humanizeStatus(guide?.status);

  return (
    <GuidePageContainer>
      {previewLabel && (
        <PreviewBanner>
          <Eye size={16} />
          {previewLabel}
        </PreviewBanner>
      )}

      <UtilityRow>
        <BackLink to="/guides">
          <ArrowLeft size={18} />
          Back to Guides
        </BackLink>

        {showEditButton && editTarget && (
          <EditLink to={editTarget}>
            <Edit3 size={16} />
            Edit in Admin
          </EditLink>
        )}
      </UtilityRow>

      <HeroCard>
        {guide?.cover_image_url && (
          <CoverImage src={guide.cover_image_url} alt={guide?.title || 'Guide cover'} loading="eager" />
        )}

        <HeroContent>
          <MetaChips>
            <MetaChip>Guides</MetaChip>
            {previewLabel && <MetaChip $tone="preview">Preview</MetaChip>}
            {statusLabel && <MetaChip $tone="status">{statusLabel}</MetaChip>}
          </MetaChips>

          <Title>{guide?.title || 'Guide'}</Title>

          {guide?.excerpt && <Excerpt>{guide.excerpt}</Excerpt>}

          <MetaInfoRow>
            {publishedLabel && (
              <MetaInfo>
                <CalendarDays size={16} />
                {`Published ${publishedLabel}`}
              </MetaInfo>
            )}

            <MetaInfo>
              <Clock3 size={16} />
              {`${readTimeMinutes} min read`}
            </MetaInfo>

            {updatedLabel && updatedLabel !== publishedLabel && (
              <MetaInfo>
                <Edit3 size={16} />
                {`Updated ${updatedLabel}`}
              </MetaInfo>
            )}
          </MetaInfoRow>
        </HeroContent>
      </HeroCard>

      <BodyCard>
        <ArticleBody
          className="ql-editor"
          dangerouslySetInnerHTML={{
            __html: guide?.content_html || '<p>This guide does not have any content yet.</p>',
          }}
        />
      </BodyCard>

      <Footer>
        <strong>&copy; 2026 micropinouts.com</strong><br />
        All rights reserved.<br />
        Educational use and community sharing encouraged under applicable open-source licenses.
      </Footer>
    </GuidePageContainer>
  );
};

export default GuideArticleView;
